import { FC, useState, useContext, useEffect } from "react";
import { useIntl } from "react-intl";
import PageHead from "./components/pageHead";
import LoginForm from "./LoginForm";
import { styled } from "@mui/material/styles";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  ColumnHeader,
  Disclosures,
  HelpContact,
  LocaleView,
} from "./components";
import { ButtonTab, Link } from "@repo/ui";
import {
  FINANCIAL,
  INSTITUTIONAL,
  STEP_1_INSTITUTIONAL_SITE_LOGIN,
} from "./constants";
import { audienceToPath, INSTITUTIONAL_SITE_LOGIN } from "@repo/utils";
import { contentLayoutConfig } from "./config";
import {
  clickEvent,
  pageLoadCompletedEvent,
  pageLoadStartedEvent,
  PartialAnalyticPageLoadStartedType,
} from "@repo/analytics";
import { AudienceContext } from "@repo/contexts";
import {
  buildQueryString,
  buildHref,
  NORTH_AMERICA,
  EUROPE_ASIA,
} from "@repo/utils";

/* 
make this a share component when consolidating 
forgot password, forgot username, register into 
this repo
*/
const StyledPageWrapper = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  padding: "60px 30px",
}));

const StyledSupportTextTypography = styled(Typography)(({ theme }) => ({
  "> a": {
    color: theme.colors.sapphire,
    textDecoration: "none",
  },
})) as typeof Typography;

/**
 * Support text is EUA-only copy. It lives in its own component so the lookup
 * runs only when rendered — inlining it would resolve the message on every
 * locale, including ones without the key.
 */
const SupportText: FC = () => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <StyledSupportTextTypography
      variant="body2"
      component="p"
      fontFamily={theme.fonts.bold}
      dangerouslySetInnerHTML={{
        __html: intl.formatMessage({ id: "helpText" }),
      }}
    />
  );
};

const Login: FC = () => {
  const { audience, country, paths } = useContext(AudienceContext);
  const intl = useIntl();
  const theme = useTheme();
  const [roleValue, setRoleValue] = useState(INSTITUTIONAL);
  const queryParams = buildQueryString(["next", "region"]);
  const registerHref = buildHref(paths.register, queryParams);
  const advisorHref = buildHref(
    "/advisor/identity/login",
    queryParams,
    "next=%2Fus%2F",
  );

  useEffect(() => {
    pageLoadStartedEvent({
      page: {
        pageName: `login > ${STEP_1_INSTITUTIONAL_SITE_LOGIN}`,
        siteAudience: audienceToPath[audience],
        siteCountry: country,
        siteSection1: INSTITUTIONAL_SITE_LOGIN,
        siteSection2: STEP_1_INSTITUTIONAL_SITE_LOGIN,
      },
    } as PartialAnalyticPageLoadStartedType);
    pageLoadCompletedEvent();
  }, [audience, country]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const isInstitutional = newValue === INSTITUTIONAL;
    setRoleValue(newValue);
    clickEvent({
      customLinkName: isInstitutional
        ? `${INSTITUTIONAL_SITE_LOGIN}_${INSTITUTIONAL} Professionals`
        : `${INSTITUTIONAL_SITE_LOGIN}_${FINANCIAL} Advisor`,
      linkId: isInstitutional
        ? intl.formatMessage({ id: "institutionalBtnTxt" })
        : intl.formatMessage({ id: "financialBtnTxt" }),
      linkPosition: "",
    });
  };

  const handleRegistrationClick = () => {
    clickEvent({
      customLinkName: `${INSTITUTIONAL_SITE_LOGIN}_Register`,
      linkId: intl.formatMessage({ id: "loginLinkText" }),
      linkPosition: "",
    });
  };

  const handleAdvisorLoginClick = () => {
    clickEvent({
      customLinkName: `${INSTITUTIONAL_SITE_LOGIN}_Advisor Login`,
      linkId: intl.formatMessage({ id: "advisorLoginText" }),
      linkPosition: "",
    });
  };

  return (
    <>
      <PageHead
        title={intl.formatMessage({ id: "login" })}
        description={intl.formatMessage({ id: "loginDescription" })}
      />
      <StyledPageWrapper>
        <Grid
          container
          columnSpacing={1}
          maxWidth={contentLayoutConfig[audience].maxWidth}
          mb={"90px"}
        >
          <Grid item md={contentLayoutConfig[audience].leftColumn} sm={12}>
            <ColumnHeader
              overline={intl.formatMessage({ id: "heading" })}
              title={intl.formatMessage({ id: "welcomeText" })}
            />
            <LocaleView audience={audience} locales={[NORTH_AMERICA]}>
              <Typography variant="subtitle1" component="div">
                {intl.formatMessage({ id: "subText" })}
              </Typography>
              <div>
                <Link onClick={handleRegistrationClick} href={registerHref}>
                  <Typography variant="subtitle1">
                    {intl.formatMessage({ id: "loginLinkText" })}
                  </Typography>
                </Link>
              </div>
            </LocaleView>
          </Grid>
          <Grid
            item
            md={contentLayoutConfig[audience].rightColumn}
            sm={12}
            pt={{ sm: 7, md: 0 }}
          >
            <LocaleView audience={audience} locales={[NORTH_AMERICA]}>
              <Typography color={theme.colors.black} variant="overline">
                {intl.formatMessage({ id: "text" })}
              </Typography>
              <Typography variant="body2" component="p" mt="26px">
                {intl.formatMessage({ id: "roleSelect" })}
              </Typography>
              <Box mt={3.75}>
                <TabContext value={roleValue}>
                  <TabList
                    TabIndicatorProps={{ hidden: true }}
                    onChange={handleChange}
                    aria-label="professional role tabs"
                  >
                    <ButtonTab
                      label={intl.formatMessage({ id: "institutionalBtnTxt" })}
                      value={INSTITUTIONAL}
                    />
                    <ButtonTab
                      label={intl.formatMessage({ id: "financialBtnTxt" })}
                      value={FINANCIAL}
                    />
                  </TabList>
                  <TabPanel sx={{ p: 0 }} value={INSTITUTIONAL}>
                    <>
                      <LoginForm />
                      <HelpContact
                        number={intl.formatMessage({
                          id: "institutionalProfessionalContact",
                        })}
                      />
                    </>
                  </TabPanel>
                  <TabPanel sx={{ p: 0 }} value={FINANCIAL}>
                    <Box sx={{ m: "43px 0", width: 430 }}>
                      <Typography
                        variant="subtitle1"
                        fontFamily={theme.fonts.regular}
                        component="div"
                      >
                        <Link
                          onClick={handleAdvisorLoginClick}
                          href={advisorHref}
                          style={{ fontFamily: theme.fonts.regular }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="span"
                            fontFamily={theme.fonts.regular}
                            fontSize="14px"
                          >
                            {intl.formatMessage({ id: "advisorLoginText" })}
                          </Typography>
                        </Link>{" "}
                        {intl.formatMessage({
                          id: "financialProfessionalLoginTxt",
                        })}
                      </Typography>
                    </Box>
                    <HelpContact
                      number={intl.formatMessage({
                        id: "financialProfessionalContact",
                      })}
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </LocaleView>
            <LocaleView audience={audience} locales={[EUROPE_ASIA]}>
              <LoginForm />
              <SupportText />
            </LocaleView>
          </Grid>
        </Grid>
        <LocaleView audience={audience} locales={[NORTH_AMERICA]}>
          <Box sx={{ mt: 0.75 }}>
            <Disclosures />
          </Box>
        </LocaleView>
      </StyledPageWrapper>
    </>
  );
};

export default Login;
